import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, Stack, useTheme } from '@mui/material';
import { useChannel } from 'ably/react';

import { CfsApi } from '@/api';
import { UnitAgencyActionModal } from '@/components/Modals/UnitShiftListModal/components/UnitAgencyActionModal';
import { AssignedUnitShiftItem } from '@/contexts';
import { withLoader } from '@/hocs';
import { useCFSContext, useUnitListFilter, useUnitShiftContext } from '@/hooks';
import { AssignedUnitShiftStatus, IAssignedUnitShift } from '@/models';
import { getAccountHeadquarters, getUnitShiftAddressLabel } from '@/services';
import {
  ConfirmTypes,
  useAgencyStore,
  useToastStore,
  useUserStore,
} from '@/store';

import { AssignedUnitItem } from './AssignedUnitItem';
import { UnitAccordion } from './UnitAccordion';
import { UnitItem } from './UnitItem';
import { CommonUnitsProps } from './UnitShiftList';
import {
  getHeadquarterNameFromAction,
  getUnitShiftCallActionItems,
  getUnitShiftHQActionItems,
  unitShiftActionItems,
} from '../data';

const AssignedUnits: FC<CommonUnitsProps> = ({
  cfsId,
  filterValues,
  searchText,
  showLoader,
  hideLoader,
  handleAssignUnit,
}) => {
  const [queryParams] = useSearchParams();
  const { updateToast } = useToastStore();
  const { cfs, unitShiftDistances } = useCFSContext();
  const { assignedUnitShifts, unitShiftAddresses, fetchAssignedUnitShifts } =
    useUnitShiftContext();
  const { account } = useUserStore();
  const headquarters = useMemo(
    () => getAccountHeadquarters(account),
    [account],
  );
  const { channel } = useChannel(
    `account:${account?._id}:cfs:${cfsId || 'all'}`,
    () => {
      fetchAssignedUnitShifts();
    },
  );

  const [expanded, setExpanded] = useState<string[]>([]);
  const agencyId = queryParams.get('agencyId');
  const { agencies } = useAgencyStore();
  const [
    showAssignUnitToUnassignedAgency,
    setShowAssignUnitToUnassignedAgency,
  ] = useState(false);
  const [unitAgencyToAssign, setUnitAgencyToAssign] = useState<
    string | undefined
  >('');
  const [, setUnitShiftToAssign] = useState<IAssignedUnitShift>();
  const [reassignAction, setReassignAction] = useState<string | number>('');
  const [reassignSelectedCfsId, setReassignSelectedCfsId] =
    useState<string>('');
  const [reassignUnitShiftId, setReassignUnitShiftId] = useState<string>('');
  const [unitShiftIdState, setUnitShiftIdState] = useState<
    string | undefined
  >();
  const [reassignState, setReassignState] = useState<boolean>();
  const [unitReassignMessage, setUnitReassignMessage] =
    useState<string>('Reassigning');

  const theme = useTheme();

  const { currentCallUnitShifts, otherCallUnitShifts, canceledAndOpenShifts } =
    useMemo(() => {
      const otherUnitShifts: AssignedUnitShiftItem[] = [];
      const currentUnitShifts: AssignedUnitShiftItem[] = [];
      const canceledAndOpenUnitShifts: AssignedUnitShiftItem[] = [];

      assignedUnitShifts.forEach((assignedUnitShift) => {
        if (cfsId && cfsId === String(assignedUnitShift.cfsId)) {
          if (
            assignedUnitShift.status === AssignedUnitShiftStatus.COMPLETED ||
            assignedUnitShift.status === AssignedUnitShiftStatus.CANCELED
          ) {
            canceledAndOpenUnitShifts.push(assignedUnitShift);
          } else {
            currentUnitShifts.push(assignedUnitShift);
          }
        } else {
          if (
            assignedUnitShift.status !== AssignedUnitShiftStatus.COMPLETED &&
            assignedUnitShift.status !== AssignedUnitShiftStatus.CANCELED &&
            (!agencyId || assignedUnitShift.unitShift.unit.agency === agencyId)
          ) {
            otherUnitShifts.push(assignedUnitShift);
          }
        }
      });

      if (cfs?.isClosed && cfs?.assignedUnitShifts) {
        currentUnitShifts.length = 0;
        canceledAndOpenUnitShifts.length = 0;

        cfs.assignedUnitShifts.forEach((assignedUnitShift) => {
          if (
            assignedUnitShift.status === AssignedUnitShiftStatus.COMPLETED ||
            assignedUnitShift.status === AssignedUnitShiftStatus.CANCELED
          ) {
            canceledAndOpenUnitShifts.push(assignedUnitShift);
          } else {
            currentUnitShifts.push(assignedUnitShift);
          }
        });
      }

      return {
        currentCallUnitShifts: currentUnitShifts,
        otherCallUnitShifts: otherUnitShifts,
        canceledAndOpenShifts: canceledAndOpenUnitShifts,
      };
    }, [assignedUnitShifts, searchText, cfs, cfsId, agencyId]);

  const currentCallUnitShiftData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: currentCallUnitShifts,
    searchText,
    searchResources: true,
  }) as AssignedUnitShiftItem[];

  const otherCallUnitShiftData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: otherCallUnitShifts,
    searchText,
    searchResources: true,
  }) as AssignedUnitShiftItem[];

  const canceledAndOpenUnitShiftsData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: canceledAndOpenShifts,
    searchText,
  }) as AssignedUnitShiftItem[];

  useEffect(() => {
    if (currentCallUnitShiftData.length) {
      setExpanded((e) => [...e, 'current-calls']);
    }

    if (otherCallUnitShifts.length) {
      setExpanded((e) => [...e, 'other-calls']);
    }
  }, [currentCallUnitShiftData, otherCallUnitShiftData]);

  const getAccordionItemCurrentValue = (value: string) => {
    return expanded.includes(value) ? value : '';
  };

  const unitShiftSortFn = (
    a: AssignedUnitShiftItem,
    b: AssignedUnitShiftItem,
  ) => {
    if (!cfs?.assignedAgencies) return 0; // no sort if no assignedAgencies

    const aHasAgency = cfs.assignedAgencies.includes(a.unitShift?.unit?.agency);
    const bHasAgency = cfs.assignedAgencies.includes(b.unitShift?.unit?.agency);

    return Number(bHasAgency) - Number(aHasAgency);
  };

  const sortedCurrentCallUnits = useMemo(() => {
    return currentCallUnitShiftData.sort(unitShiftSortFn);
  }, [currentCallUnitShiftData, cfs?.assignedAgencies]);

  const sortedCanceledAndOpenUnits = useMemo(() => {
    return canceledAndOpenUnitShiftsData.sort(unitShiftSortFn);
  }, [canceledAndOpenUnitShiftsData, cfs?.assignedAgencies]);

  const sortedOtherCallUnits = useMemo(() => {
    return otherCallUnitShiftData.sort(unitShiftSortFn);
  }, [otherCallUnitShiftData, cfs?.assignedAgencies]);

  const handleExpand = (panel: string) => {
    if (expanded.includes(panel))
      setExpanded((e) => e.filter((v) => v !== panel));
    else {
      setExpanded((e) => [...e, panel]);
    }
  };

  const checkAndAssignAvailableUnitShift = (
    assignedUnitShift: IAssignedUnitShift,
    action: string | number,
    selectedCfsId = '',
    assignedUnitShiftId = '',
    unitShiftId?: string,
    reassign?: boolean,
  ) => {
    if (
      cfs?.assignedAgencies?.includes(assignedUnitShift.unitShift.unit.agency)
    ) {
      handleSelectStatus(
        action,
        selectedCfsId,
        assignedUnitShiftId,
        unitShiftId,
        reassign,
      );
      return;
    }

    if (action === 'QUEUED') {
      setUnitReassignMessage('Queuing');
    } else {
      setUnitReassignMessage('Reassigning');
    }

    if (agencies) {
      const foundAgency = agencies.find(
        (agency) => agency._id === assignedUnitShift.unitShift.unit.agency,
      );

      setUnitAgencyToAssign(foundAgency?.name);
      setUnitShiftToAssign(assignedUnitShift);
      setReassignAction(action);
      setReassignSelectedCfsId(selectedCfsId);
      setReassignUnitShiftId(assignedUnitShiftId);
      setUnitShiftIdState(unitShiftId);
      setReassignState(reassign);

      if (reassign) {
        setShowAssignUnitToUnassignedAgency(true);
      }
    }
  };

  const handleSelectStatus = async (
    action: string | number,
    selectedCfsId = '',
    assignedUnitShiftId = '',
    unitShiftId?: string,
    reassign?: boolean,
  ) => {
    showLoader();
    try {
      let successMsg = '';
      const headquarterName = getHeadquarterNameFromAction(action);

      if (headquarterName || action === 'assignToCall') {
        await CfsApi.updateAssignedUnitShiftDispatchedToHQStatus(
          selectedCfsId,
          assignedUnitShiftId,
          headquarterName,
        );
      } else {
        if (cfsId && action === AssignedUnitShiftStatus.QUEUED) {
          await CfsApi.addUnitShiftToQueue(cfsId, String(unitShiftId));
        } else {
          await CfsApi.updateAssignedUnitShiftStatus(
            selectedCfsId,
            assignedUnitShiftId,
            { status: action as AssignedUnitShiftStatus },
          );
          if (action === 'COMPLETED' || action === 'CANCELED') {
            const assignedUS = assignedUnitShifts.find(
              (unit) => unit._id === assignedUnitShiftId,
            );
            assignedUS?.unitShift?.users?.forEach((user) => {
              channel?.publish(`closed:cfs:${cfsId}:user:${user._id}`, {
                id: cfsId,
                event: 'assignmentEnded',
              });
            });
          }
        }
      }

      switch (action) {
        case 'assignToCall':
          successMsg = 'Unit shift is assigned to a call';
          break;
        case AssignedUnitShiftStatus.EN_ROUTE:
          successMsg = 'Unit shift is now en route';
          break;
        case AssignedUnitShiftStatus.IN_THE_AREA:
          successMsg = 'Unit shift is in the area';
          break;
        case AssignedUnitShiftStatus.ON_SCENE:
          successMsg = 'Unit shift has arrived on scene';
          break;
        case AssignedUnitShiftStatus.PROGRESS:
          successMsg = 'Unit shift assignment is now in progress';
          break;
        case AssignedUnitShiftStatus.COMPLETED:
          successMsg = 'Unit shift assignment completed';
          break;
        case AssignedUnitShiftStatus.ASSIGNED:
          successMsg = 'Unit shift assigned';
          break;
        case AssignedUnitShiftStatus.CANCELED:
          successMsg = 'Unit shift assignment canceled';
          break;
        case AssignedUnitShiftStatus.QUEUED:
          successMsg = 'Unit shift assignment queued';
          break;
      }

      if (headquarterName) {
        successMsg = `Unit shift is assigned to ${headquarterName}`;
      }

      if (
        reassign &&
        cfsId &&
        unitShiftId &&
        action !== AssignedUnitShiftStatus.QUEUED
      ) {
        await CfsApi.assignUnitShift(cfsId, unitShiftId);
        successMsg = 'Unit shift successfully reassigned to the current call';
      }
      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: successMsg,
      });
      handleAssignUnit?.();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  if (!cfsId) {
    return (
      <Stack flex={1} sx={{ padding: '15px', overflow: 'auto' }}>
        {otherCallUnitShiftData.map((item, index) => {
          const unitShiftActionItemsForAssignedUnit = item.isDispatchedToHQ
            ? getUnitShiftHQActionItems(headquarters, item.headquarterName)
            : item.unitShift.unit.type.isDispatchToHQEnabled
            ? getUnitShiftCallActionItems(headquarters)
            : unitShiftActionItems;

          return (
            <AssignedUnitItem
              {...item}
              key={index}
              isCfsClosed={cfs?.isClosed}
              unitShiftActionItems={unitShiftActionItemsForAssignedUnit}
              handleSelectAction={(value) => {
                handleSelectStatus(value, item.cfsId, item._id);
                handleAssignUnit?.();
              }}
            />
          );
        })}
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(!!currentCallUnitShiftData.length ||
        !!canceledAndOpenShifts.length) && (
        <UnitAccordion
          title="Current call"
          value="current-calls"
          badge={currentCallUnitShiftData.length}
          currentValue={getAccordionItemCurrentValue('current-calls')}
          handleExpand={handleExpand}
        >
          {sortedCurrentCallUnits.map((assignedUnitShift) => (
            <AssignedUnitItem
              {...assignedUnitShift}
              key={assignedUnitShift._id}
              isCfsClosed={cfs?.isClosed}
              unitShiftActionItems={
                assignedUnitShift.isDispatchedToHQ
                  ? getUnitShiftHQActionItems(
                      headquarters,
                      assignedUnitShift.headquarterName,
                    )
                  : assignedUnitShift.unitShift.unit.type.isDispatchToHQEnabled
                  ? getUnitShiftCallActionItems(headquarters)
                  : unitShiftActionItems
              }
              handleSelectAction={(value) => {
                handleSelectStatus(
                  value,
                  assignedUnitShift.cfsId,
                  assignedUnitShift._id,
                );
                handleAssignUnit?.();
              }}
            />
          ))}
          {sortedCanceledAndOpenUnits.map((assignedUnit, index) => {
            const status = assignedUnit.status;
            return (
              <UnitItem
                key={index}
                {...assignedUnit.unitShift?.unit}
                agency={agencies.find(
                  (agency) => agency._id === assignedUnit.unitShift.unit.agency,
                )}
                addressLabel={getUnitShiftAddressLabel(
                  assignedUnit?.unitShift,
                  unitShiftAddresses,
                )}
                distance={
                  unitShiftDistances[assignedUnit.unitShift?._id as string]
                }
                resources={{
                  polygons: assignedUnit.unitShift.polygons,
                }}
                rootProps={{
                  sx: { backgroundColor: theme.palette.divider },
                }}
                hideDetail
              >
                <Box className="name-box status-box">
                  {status.toLowerCase()}
                </Box>
              </UnitItem>
            );
          })}
        </UnitAccordion>
      )}
      {!!otherCallUnitShiftData.length && (
        <UnitAccordion
          title="Other calls"
          value="other-calls"
          badge={otherCallUnitShiftData.length}
          currentValue={getAccordionItemCurrentValue('other-calls')}
          handleExpand={handleExpand}
        >
          {sortedOtherCallUnits.map((assignedUnitShift) => (
            <AssignedUnitItem
              {...assignedUnitShift}
              isCfsClosed={cfs?.isClosed}
              key={assignedUnitShift._id}
              unitShiftActionItems={[]}
              handleSelectAction={(value, reassign) => {
                checkAndAssignAvailableUnitShift(
                  assignedUnitShift,
                  value,
                  assignedUnitShift.cfsId,
                  assignedUnitShift._id,
                  assignedUnitShift.unitShift._id,
                  reassign,
                );
                handleAssignUnit?.();
              }}
              reassign
            />
          ))}
        </UnitAccordion>
      )}
      <UnitAgencyActionModal
        open={showAssignUnitToUnassignedAgency}
        mode="reassign"
        agencyName={unitAgencyToAssign}
        onCancel={() => setShowAssignUnitToUnassignedAgency(false)}
        onConfirm={() => {
          handleSelectStatus(
            reassignAction,
            reassignSelectedCfsId,
            reassignUnitShiftId,
            unitShiftIdState,
            reassignState,
          );
          setShowAssignUnitToUnassignedAgency(false);
        }}
        message={unitReassignMessage}
      />
    </Box>
  );
};

export default withLoader(AssignedUnits);
